import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CreditCard, Loader2, ExternalLink, Crown, Sparkles, Calendar } from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { authFetch } from "../lib/auth";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_LABEL = {
  trial: "Free trial",
  trial_expired: "Trial expired",
  active: "Active subscription",
  expired: "Subscription expired",
  memory_school: "Memory School member",
  comped: "Free for life",
};

const SubscriptionPanel = () => {
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(`${API}/subscription/me`);
        if (res.ok && !cancelled) setSub(await res.json());
      } catch (e) {
        // silent
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const openPortal = async () => {
    setOpening(true);
    try {
      const res = await authFetch(`${API}/subscription/portal`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ origin_url: window.location.origin }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Could not open portal");
      }
      const data = await res.json();
      if (data.url) window.location.href = data.url;
    } catch (e) {
      toast.error(e.message || "Could not open billing portal");
      setOpening(false);
    }
  };

  if (loading) return null;
  if (!sub) return null;

  const status = sub.subscription_status;
  const label = STATUS_LABEL[status] || status;
  const isActiveSub = status === "active" || status === "expired";
  const isComped = status === "comped";
  const isMS = status === "memory_school";
  const expiresAt = sub.subscription_expires_at
    ? new Date(sub.subscription_expires_at)
    : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="subscription-panel"
    >
      <Card className="bg-card border-border">
        <CardContent className="p-6">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 flex items-center justify-center rounded-full ${
                isComped ? 'bg-primary/15' : isMS ? 'bg-secondary/15' : 'bg-muted'
              }`}>
                {isComped ? (
                  <Crown className="w-5 h-5 text-primary" />
                ) : isMS ? (
                  <Sparkles className="w-5 h-5 text-secondary" />
                ) : (
                  <CreditCard className="w-5 h-5 text-muted-foreground" />
                )}
              </div>
              <div>
                <h3 className="font-heading text-lg font-semibold">Subscription</h3>
                <p className="text-sm text-muted-foreground" data-testid="subscription-status-label">
                  {label}
                </p>
              </div>
            </div>
          </div>

          {/* Trial countdown */}
          {status === "trial" && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
              <Calendar className="w-4 h-4" />
              <span>
                {sub.days_remaining} day{sub.days_remaining === 1 ? "" : "s"} left in your free trial
              </span>
            </div>
          )}

          {/* Active subscription */}
          {isActiveSub && expiresAt && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
              <Calendar className="w-4 h-4" />
              <span>
                {sub.cancel_at_period_end ? "Ends" : "Renews"} on{" "}
                {expiresAt.toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" })}
              </span>
            </div>
          )}

          {/* Comped */}
          {isComped && (
            <p className="text-sm text-muted-foreground mb-4">
              You unlocked free-for-life by referring 3 friends. Thank you 👑
            </p>
          )}

          {/* Memory school */}
          {isMS && (
            <p className="text-sm text-muted-foreground mb-4">
              Your access is included with your Memory School membership.
            </p>
          )}

          {/* Action buttons */}
          <div className="flex flex-col sm:flex-row gap-2">
            {(status === "trial" ||
              status === "trial_expired" ||
              status === "expired") && (
              <Button
                onClick={() => navigate("/paywall")}
                data-testid="subscription-upgrade-btn"
                className="bg-primary text-primary-foreground hover:bg-primary/90"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                {status === "trial" ? "Upgrade now" : "Choose a plan"}
              </Button>
            )}
            {isActiveSub && (
              <Button
                onClick={openPortal}
                disabled={opening}
                data-testid="manage-subscription-btn"
                variant="outline"
              >
                {opening ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Opening…
                  </>
                ) : (
                  <>
                    Manage subscription
                    <ExternalLink className="w-3.5 h-3.5 ml-2 opacity-60" />
                  </>
                )}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default SubscriptionPanel;
